import { JobHtmlRenderer } from '@seek/job-html-renderer';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Drawer,
  Loader,
  PageBlock,
  Stack,
  Text,
} from 'braid-design-system';
import type { ReactNode } from 'react';
import { Outlet } from 'react-router-dom';

import { Page, PageSection, useTrackingClient } from 'src/context/tracking';
import { useJobDetails } from 'src/hooks/useJobDetails';
import { useDialogRouterState } from 'src/modules/use-dialog-router-state';

import translations from '../.vocab';

import PageTitle from './PageTitle';

interface Props {
  titleRef: React.RefObject<HTMLElement | null>;
  header: ReactNode;
  children?: ReactNode;
}

export const ApplyLayout = ({ titleRef, header, children }: Props) => {
  const { t } = useTranslations(translations);
  const { trackViewJobDetails } = useTracking();

  const {
    isOpen,
    open: openJobDetails,
    close: closeJobDetails,
  } = useDialogRouterState('view-job');

  const handleTitleClick = () => {
    openJobDetails();
    trackViewJobDetails(true);
  };

  const handleJobDetailsClose = () => {
    closeJobDetails();
    trackViewJobDetails(false);
  };

  const { loading, data: jobDetails } = useJobDetails();

  if (loading || !jobDetails) {
    return (
      <Stack space="none" align="center">
        <Box padding="medium">
          <Loader />
        </Box>
      </Stack>
    );
  }

  return (
    <PageBlock width="medium">
      <Box ref={titleRef}>
        <PageTitle jobDetails={jobDetails} onClick={handleTitleClick} />
      </Box>
      {header}
      <Box marginTop="xxlarge" maxWidth="small">
        {children ? children : <Outlet />}
      </Box>
      <Drawer
        id="jobDescription"
        title={jobDetails.title}
        description={<Text size="large">{jobDetails.advertiser.name}</Text>}
        position="right"
        open={isOpen}
        closeLabel={t('Close job description')}
        onClose={handleJobDetailsClose}
      >
        <Text size="small" tone="secondary">
          {t('Posted', { createdAt: jobDetails.createdAt.shortLabel })}
        </Text>
        <JobHtmlRenderer key="jobAdFullDescription" background="white">
          {jobDetails.content}
        </JobHtmlRenderer>
      </Drawer>
    </PageBlock>
  );
};

function useTracking() {
  const client = useTrackingClient();
  const trackViewJobDetails = (isExpanded: boolean) => {
    client.trackLink({
      link: `apply-job-details-${isExpanded ? 'expand' : 'collapse'}`,
      eventName: `job_details_${
        isExpanded ? 'expand_pressed' : 'collapse_pressed'
      }`,
      currentPage: Page.DOCUMENT_PAGE,
      currentPageSection: PageSection.JOB_DETAILS,
      isExitLink: false,
    });
  };

  return { trackViewJobDetails };
}
